"use client";
import { Button, Typography } from "antd";
import CardChevron from "@/Components/Common/CardChevron";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Image from "next/image";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { openTimerModelAction } from "../../../Actions/UIActions/FmlmViewInformation/OpenTimerModelAction";
import CalendarComponent from "../../../Components/Feature/Calendar/Calendar";
import { Analytics } from "../../../Lib/Analytics/events";
import { getSearchSelectedPassenger } from "../../../Lib/Utils/passenger";
import { getMaxAllowedInfants } from "../../../Lib/Utils/passengerValidation";
import { isEmpty, arabicDayNames, formatDateByLocale } from "../../../Lib/Utils/validationUtil";
import { evtTree, trainOfferEvtTree } from "../../../Orchestrator/ConstantTree";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import {
  ISearchComponentProps,
  SearchRequestType,
  SearchStateType,
  StationsListType,
} from "../../../Orchestrator/Types/Index";
import { IPassengerSelection } from "../../../Orchestrator/Types/Interface/IPassenger";
import { showNotification } from "../../../Services/notificationService";
import { _eventBus } from "../../../core/events/EventBus";
import TimeSelectModal from "../../TrainOffer/FmlmTrainOffer/TimeSelectModal";
import LocationPopup from "../FromDropDown/LocationPopup";
import PassengerSelector from "../PassengerSelector/PassengerSelector";
import "./search-trip.css";
import stopManager from "../../../Lib/Utils/StopManager";

const SearchComponent: React.FC<ISearchComponentProps> = ({
  className,
  setIsOpenModal,
  isFromSearchPage = false,
  isFromPopUp = false,
}) => {
  const intl = useIntl();
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [searchPopupData] = useRecState<SearchStateType>("searchStatePopupAtom");
  const [isPassengerDropdownOpen] = useRecState<boolean>("isPassengerDropdownOpenAtom");
  const [locations] = useRecState<StationsListType>("stopsResponseAtom");
  const [passengerTypes] = useRecState<IPassengerSelection[]>("passengerTypesStateAtom");
  const [isSelectingOrigin] = useRecState<boolean>("isSelectingOriginAtom");
  const [isSelectingDestination] = useRecState<boolean>("isSelectingDestinationAtom");
  const [isTimerModalOpen] = useRecState<boolean>("isTimerVisibleAtom");
  const [resumeSearch, setResumeSearch] = React.useState<SearchRequestType | undefined>(undefined);
  const [, setStopsReadyTick] = React.useState(0);
  const [calendarDayInterval] = useRecState<number>("calendarDayIntervalAtom");
  const {
    totalPassengerCount,
    passengerCounts,
    passengerSummary,
    totalWithoutInfants,
    allPassengerSummary
  } = getSearchSelectedPassenger(search.passengers, intl);

  // Arabic only: the single-type count label comes back as "2x بالغ", but the
  // Latin "x" multiplier reads wrong in the RTL search bar — swap the digit and
  // the x so it shows "x2 بالغ". The multi-passenger form ("3 الركاب") has no
  // "x", so the regex leaves it untouched.
  const passengerSummaryDisplay =
    intl.locale === "ar" && allPassengerSummary
      ? allPassengerSummary.replace(/^(\d+)x\s/, "x$1 ")
      : allPassengerSummary;
  const [trainType] = useRecState<keyof typeof TrainTypeEnum>("trainTypeAtom");

  const disabledDates = (current: Dayjs) =>
    current.isBefore(dayjs(), "day") ||
    current.isAfter(dayjs().add(calendarDayInterval, "day"), "day");

  dayjs.extend(utc);
  dayjs.extend(timezone);
const getDateFormat = () => {
  if (intl.locale === "ar") {
    return (date: Dayjs) => {
      if (!date) return "";
      const dayName = arabicDayNames[date.locale("en").format("ddd")]; 
      const day = date.format("DD");
      const month = date.format("MM");
      return `${dayName}، ${day}/${month}`;
    };
  }
  return "ddd, DD MMM";
};

  const handleCancel = () => {
    openTimerModelAction.setParams({ isTimerVisible: false })
    openTimerModelAction.execute()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the "from" dropdown
      if (isSelectingOrigin && !(event.target as HTMLElement).closest(".from.search_items")) {
        _eventBus.emit({
          eventName: evtTree.SearchBar.SelectOrigin.Click,
          data: { outsideClick: true },
        });
      }

      // Check if click is outside the "to" dropdown
      else if (isSelectingDestination && !(event.target as HTMLElement).closest(".to.search_items")) {
        _eventBus.emit({
          eventName: evtTree.SearchBar.SelectDestination.Click,
          data: { outsideClick: true },
        });
      }

      // Check if click is outside the "passenger" dropdown
      if (isPassengerDropdownOpen && !(event.target as HTMLElement).closest(".passengers_col")) {
        _eventBus.emit({
          eventName: evtTree.SearchBar.SelectPassenger.OpenPopUp,
          data: isPassengerDropdownOpen,
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSelectingOrigin, isSelectingDestination, isPassengerDropdownOpen]);

  const handleDisabled = (): boolean => {
    const validPassengers =
      Object.entries(passengerCounts)
        .filter(([key]) => key !== "children" && key !== "infants")
        .every(([, value]) => value > 0) &&
      Object.keys(passengerCounts).some((key) => key !== "children" && key !== "infants");

    // Check if infant count exceeds the allowed ceiling (adults + seniors only)
    const infantCount = search.passengers?.infants || 0;
    const maxAllowedInfants = getMaxAllowedInfants(search.passengers || {});
    const invalidInfantCount = infantCount > maxAllowedInfants && infantCount > 0;

    return (
      [search.outboundDate, search.inboundDate].filter((d) => d === null).length === 2 ||
      totalPassengerCount === 0 ||
      !validPassengers ||
      invalidInfantCount
    );
  };

  const isSameSearchLocation = (searchState: SearchStateType): boolean => {
    const { from, to, fromGeoPosition, toGeoPosition } = searchState;
    if (fromGeoPosition && toGeoPosition && fromGeoPosition.latitude && toGeoPosition.latitude) {
      return (
        fromGeoPosition.latitude === toGeoPosition.latitude &&
        fromGeoPosition.longitude === toGeoPosition.longitude
      );
    }

    if (from && to && from === to) return true

    return false
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    let resumeSearchObj = localStorage.getItem("resumeSearch");
    if (resumeSearchObj) {
      const parsed = JSON.parse(resumeSearchObj);
      const today = dayjs().startOf('day');

      const arrival = parsed.arrivalTime ? dayjs(parsed.arrivalTime).startOf('day') : null;
      const departure = parsed.departureTime ? dayjs(parsed.departureTime).startOf('day') : null;

      const isArrivalValid = arrival ? !arrival.isBefore(today) : true;
      const isDepartureValid = departure ? !departure.isBefore(today) : true;

      if (isArrivalValid && isDepartureValid) {
        setResumeSearch(parsed);
      } else {
        localStorage.removeItem("resumeSearch");
        setResumeSearch(undefined);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    stopManager.fetchStops().finally(() => {
      if (isMounted) setStopsReadyTick((n) => n + 1);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className={className} dir={"ltr"}>
      <div className="top_search">
        <div className="main-search-block">
          <div className="search_block">
            <div className="search_col from-top-section">
              <div className="from search_items position-relative">
                <div>
                  <label htmlFor="from">{intl.formatMessage({ id: "from" })}</label>
                  <div
                    id="from"
                    aria-label="from"
                    onClick={async () => {
                      // Guard: re-emitting the open event while the "from"
                      // dropdown is already open forces a redundant flow step
                      // hand-off (SelectSearchStop_Step → SearchStartupInit_Step),
                      // and the next click lands in the teardown/setup window where
                      // the global EventBus silently drops it — making the dropdown
                      // unresponsive. Only emit when it is not already open.
                      if (isSelectingOrigin) return;
                      _eventBus.emit({
                        eventName: evtTree.SearchBar.SelectOrigin.Click,
                      });
                    }}
                  >
                    <span
                      className="desination-text"
                      style={{
                        ...(!isEmpty(search.fromName) || !isEmpty(searchPopupData?.fromName)
                          ? {}
                          : {
                            color: "#757575",
                            fontWeight: "normal",
                          }),
                      }}
                    >
                      {search.fromName || searchPopupData?.fromName
                        ? !isFromPopUp
                          ? stopManager.getStopNameById(search?.from || search?.fromName, search?.fromName)
                          : stopManager.getStopNameById(searchPopupData?.from || searchPopupData?.fromName, searchPopupData?.fromName)
                        : intl.formatMessage({ id: "selectOrigin" })}
                    </span>
                  </div>
                </div>
                <LocationPopup
                  // selectedLocation={isFromPopUp ? searchPopupData?.to : search.to}
                  locations={locations}
                  visible={isSelectingOrigin}
                  onSelect={(id: string, name: string, isShuttle?: boolean, geoPosition?: any) => {
                    _eventBus.emit({
                      eventName: evtTree.SearchBar.SelectOrigin.Select,
                      data: {
                        field: "from",
                        id: id,
                        name: name,
                        search: search,
                        isFromPopUp: isFromPopUp,
                        searchPopup: searchPopupData,
                        isShuttle: isShuttle,
                        geoPosition: geoPosition,
                      },
                    });
                  }}
                  locationFor={"from"}
                  isFromPopUp={isFromPopUp}
                />
              </div>
              <div className="to search_items position-relative">
                <label htmlFor="to">{intl.formatMessage({ id: "to" })}</label>
                <div
                  id="to"
                  aria-label="to"
                  onClick={async () => {
                    // Guard: re-emitting the open event while the "to" dropdown is
                    // already open forces a redundant flow step hand-off and the
                    // next click lands in the teardown/setup window where the global
                    // EventBus silently drops it — making the dropdown unresponsive.
                    // Only emit when it is not already open. See "from" above.
                    if (isSelectingDestination) return;
                    _eventBus.emit({
                      eventName: evtTree.SearchBar.SelectDestination.Click,
                    });
                  }}
                >
                  <span
                    className="desination-text"
                    style={{
                      ...(!isEmpty(search.toName) || !isEmpty(searchPopupData?.toName)
                        ? {}
                        : {
                          color: "#757575",
                          fontWeight: "normal",
                        }),
                    }}
                  >
                    {search.toName || searchPopupData?.toName
                      ? !isFromPopUp
                        ? stopManager.getStopNameById(search?.to || search?.toName, search?.toName)
                        : stopManager.getStopNameById(searchPopupData?.to || searchPopupData?.toName, searchPopupData?.toName)
                      : intl.formatMessage({ id: "selectDestination" })}
                  </span>
                </div>
                <LocationPopup
                  // selectedLocation={isFromPopUp ? searchPopupData?.from : search.from}
                  locations={locations}
                  visible={isSelectingDestination}
                  onSelect={(id: string, name: string, isShuttle?: boolean, geoPosition?: any) => {
                    _eventBus.emit({
                      eventName: evtTree.SearchBar.SelectOrigin.Select,
                      data: {
                        field: "to",
                        id: id,
                        name: name,
                        search: search,
                        isFromPopUp: isFromPopUp,
                        searchPopup: searchPopupData,
                        isShuttle: isShuttle,
                        geoPosition: geoPosition,
                      },
                    });
                  }}
                  locationFor={"to"}
                  isFromPopUp={isFromPopUp}
                />
              </div>
              <div
                className="move"
                onClick={() => {
                  _eventBus.emit({
                    eventName: evtTree.SearchBar.Select.Switch,
                    data: {
                      search: search,
                      isFromPopUp: isFromPopUp,
                      searchPopup: searchPopupData,
                    },
                  });
                }}
              >
                <Image
                  src="/assets/search_icons/arrow-left-arrow-right.svg"
                  className="direction-icon"
                  alt="switchDirection"
                  width={18}
                  height={18}
                  priority
                />
              </div>
            </div>
            <div className="search_col select-date">
              <CalendarComponent
                value={
                  isFromPopUp
                    ? [searchPopupData.outboundDate, searchPopupData.inboundDate]
                    : [search.outboundDate, search.inboundDate]
                }
                format={getDateFormat()}
                disabledDates={disabledDates}
                placeholder={[
                  intl.formatMessage({ id: "departureDateLabel" }),
                  intl.formatMessage({ id: "returnDateLabel" }),
                ]}
                returnHintTextId="chooseDate"
                width="100%"
                showTime={false}
                priceData={{
                  "2024-09-15": 25,
                  "2024-09-16": 32,
                  "2024-09-17": 95,
                }}
                onChange={(dates: [Dayjs | null, Dayjs | null]) => {
                  if (dates[0]?.isSame(dayjs(), "day")) {
                    /**
                     * Set dates[0] to current date if it is today
                     * This is to ensure that the date is set to today when the user selects it.
                     * This is important because the date picker may not always set the date to today from 00:00:00.000
                     * We have timezone issue in Dubai, so we need to set the date to today.
                     * Because 00:00:00.000 is not same as today date in Dubai timezone.
                     * So we need to set the date to today.
                     * */
                    dates[0] = dayjs();
                  }
                  _eventBus.emit({
                    eventName: evtTree.SearchBar.SelectDate.Select,
                    data: {
                      dates: dates,
                      search: search,
                      isFromPopUp: isFromPopUp,
                      searchPopup: searchPopupData,
                    },
                  });
                }}
              />
            </div>

            <div className="search_col passengers_col">
              <div className="passengers">
                <div
                  className="passenger-selector"
                  aria-label="passenger-selector"
                  id="passengers"
                  onClick={async () => {
                    _eventBus.emit({
                      eventName: evtTree.SearchBar.SelectPassenger.OpenPopUp,
                      data: isPassengerDropdownOpen,
                    });
                  }}
                >
                  <span>
                    <label htmlFor="passengers">{intl.formatMessage({ id: "passengers" })}</label>
                    <h3
                      id="passengers"
                      className="desination-text"
                      title={passengerSummaryDisplay}
                      // Arabic count label is "x2 …"; the heading's
                      // `text-transform: capitalize` would up-case the leading
                      // "x" to "X", so disable it for ar (Arabic has no case).
                      style={intl.locale === "ar" ? { textTransform: "none" } : undefined}
                    >
                      {/* {passengerSummary} */} {passengerSummaryDisplay}
                    </h3>
                  </span>
                  <span className={`arrow`}>
                    <CardChevron direction="down" width={12} height={12} />
                  </span>
                </div>
                {isPassengerDropdownOpen && (
                  <PassengerSelector
                    passengerTypes={passengerTypes}
                    totalWithoutInfants={totalWithoutInfants}
                    isFromPopUp={isFromPopUp}
                  />
                )}
              </div>              
            </div>

            <div className="search_btn">
              <Button
                type="primary"
                className="btn"
                disabled={handleDisabled()}
                onClick={async () => {
                  const activeSearch = isFromPopUp ? searchPopupData : search;
                  const originValue = activeSearch?.from;
                  const destinationValue = activeSearch?.to;
                  if (isEmpty(originValue) || isEmpty(destinationValue)) {
                    showNotification("error", intl.formatMessage({ id: "selectOriginDestination" }), "", 6);
                    return;
                  }

                  if (!activeSearch?.outboundDate) {
                    showNotification("error", intl.formatMessage({ id: "selectDepartureDate" }), "", 6);
                    return;
                  }

                  const isSameSearchState = isSameSearchLocation(search);
                  if (isSameSearchState) {
                    showNotification("error", intl.formatMessage({ id: "sameLocationError" }), "", 6);
                    return;
                  }

                  const isFmlmSearch = search.fromfmlmSearch || search.tofmlmSearch

                  // Update search state on modify search
                  if (className === "outbound-component-search") {
                    _eventBus.emit({
                      eventName: trainOfferEvtTree.Offer.ModifySearch.Click,
                      data: {
                        openModal: isFmlmSearch ? true : false,
                        search,
                        searchPopupData,
                        isPopupConfirm: true,
                        isConfirm: true,
                      },
                    });
                  }

                  if (isFmlmSearch) {
                    if (search.fromGeoPosition?.latitude && search.toGeoPosition?.latitude) {
                      _eventBus.emit({
                        eventName: evtTree.SearchBar.FmlmSearch.Click,
                        data: {
                          isTimerVisible: true,
                        },
                      });
                      return;
                    }

                    showNotification("error", intl.formatMessage({ id: "selectValidOriginAndDestination" }), "", 6);
                    return;
                  }

                  // #4 search_btn
                  const activeForAnalytics = isFromPopUp ? searchPopupData : search;
                  const presentPassengerTypes = Object.entries(passengerCounts ?? {})
                    .filter(([, count]) => Number(count) > 0)
                    .map(([type]) => type);
                  Analytics.searchTrip({
                    origin: activeForAnalytics?.from ?? "",
                    destination: activeForAnalytics?.to ?? "",
                    departureDate: activeForAnalytics?.outboundDate?.format("YYYY-MM-DD") ?? "",
                    returnDate: activeForAnalytics?.inboundDate?.format("YYYY-MM-DD") ?? "",
                    passengerType: presentPassengerTypes.join(",") || "adult",
                    passengerCount: totalPassengerCount ?? 0,
                  });

                  // else {
                  _eventBus.emit({
                    eventName: evtTree.SearchBar.SearchButton.Click,
                    data: {
                      departureTime: isFromPopUp
                        ? searchPopupData?.outboundDate?.isSame(dayjs(), "day")
                          ? dayjs().tz("Asia/Dubai").format("YYYY-MM-DDTHH:mm:ss")
                          : searchPopupData?.outboundDate?.format("YYYY-MM-DDT00:00:00.000")
                        : search.outboundDate?.isSame(dayjs(), "day")
                          ? dayjs().tz("Asia/Dubai").format("YYYY-MM-DDTHH:mm:ss")
                          : search.outboundDate?.format("YYYY-MM-DDT00:00:00.000"),

                      arrivalTime: isFromPopUp
                        ? searchPopupData.inboundDate
                          ? searchPopupData.inboundDate?.isSame(dayjs(), "day")
                            ? dayjs().tz("Asia/Dubai").format("YYYY-MM-DDTHH:mm:ss.SSS")
                            : searchPopupData.inboundDate?.format("YYYY-MM-DDT00:00:00.000")
                          : null // If no inboundDate, return null
                        : search.inboundDate
                          ? search.inboundDate?.isSame(dayjs(), "day")
                            ? dayjs().tz("Asia/Dubai").format("YYYY-MM-DDTHH:mm:ss.SSS") // Keep time if today
                            : search.inboundDate?.format("YYYY-MM-DDT00:00:00.000")
                          : null,
                      origin: isFromPopUp ? searchPopupData.from : search.from,
                      destination: isFromPopUp ? searchPopupData.to : search.to,
                      totalPassengerCount: totalPassengerCount,
                      passengerCounts: passengerCounts,
                      trainType: TrainTypeEnum.outbound,
                      //isShuttle: isShuttleBooking,
                      resetValue: isFromPopUp ? true : true,
                      fromShuttle: isFromPopUp ? searchPopupData.fromShuttle : search.fromShuttle,
                      toShuttle: isFromPopUp ? searchPopupData.toShuttle : search.toShuttle,
                      isFromPopUp: isFromPopUp,
                      intl: intl
                    },
                  });
                  // }
                }}
              >
                {className === "outbound_component"
                  ? intl.formatMessage({ id: "modify_search" })
                  : className === "outbound-component-search"
                    ? intl.formatMessage({ id: "confirmText" })
                    : intl.formatMessage({ id: "search" })}
              </Button>
              {className === "outbound-component-search" && (
                <Button
                  key="cancel"
                  type="text"
                  size="large"
                  className="ml-10 h-50 pl-40 pr-40"
                  onClick={() => {
                    _eventBus.emit({
                      eventName: trainOfferEvtTree.Offer.ModifySearchClose.Click,
                      data: {
                        openModal: false,
                        search,
                        searchPopupData,
                      },
                    });
                  }}
                >
                  {intl.formatMessage({ id: "cancelText" })}
                </Button>
              )}
            </div>
          </div>

          <TimeSelectModal
            open={isTimerModalOpen}
            handleClose={handleCancel}
            isFromPopUp={isFromPopUp}
            trainType={isFromPopUp ? TrainTypeEnum.outbound : trainType}
          />

          {isFromSearchPage && resumeSearch && <div className="last-search">
            <div className="wrapper align-items-center color-757575">
              <Typography.Text className="fs-14 fw-700 color-757575">
                {resumeSearch.fromfmlmSearch || resumeSearch.tofmlmSearch ? stopManager.getStopNameById(resumeSearch?.origin) : stopManager.getStopNameById(resumeSearch?.origin)}
              </Typography.Text>
              <Image
                src={resumeSearch?.arrivalTime ? "/search/round-grey.svg" : "/search/right-grey.svg"}
                width={20}
                height={20}
                alt="train"
                className="ml-8 mr-8"
              />
              <Typography.Text className="fs-14 fw-700 color-757575">
                {resumeSearch.fromfmlmSearch || resumeSearch.tofmlmSearch ? stopManager.getStopNameById(resumeSearch?.destination) : stopManager.getStopNameById(resumeSearch?.destination)}
              </Typography.Text>
              <span className="ml-5 mr-5">{"•"}</span>
              <Typography.Text className="fs-13 fw-700 color-757575">
                {formatDateByLocale(resumeSearch?.departureTime)}
              </Typography.Text>

              {resumeSearch?.arrivalTime && <><span className="ml-5 mr-5">{"-"}</span>
                <Typography.Text className="fs-13 fw-700 color-757575">
                  {formatDateByLocale(resumeSearch?.arrivalTime)}
                </Typography.Text></>}

              <span className="ml-5 mr-5">{"•"}</span>
              <Typography.Text
                className="fs-13 fw-700 mr-10"
                style={{ color: "#757575" }}
              >
                {resumeSearch?.totalPassengerCount} {resumeSearch?.totalPassengerCount > 1 ? intl.formatMessage({ id: "passengersText" }) : intl.formatMessage({ id: "passengerText" })}
              </Typography.Text>
              <Typography.Text className="fs-14 fw-700 continue-last-search"
                onClick={async () => {
                  if (resumeSearch.fromfmlmSearch || resumeSearch.tofmlmSearch) {
                    if (resumeSearch.fromGeoPosition?.latitude && resumeSearch.toGeoPosition?.latitude) {
                      _eventBus.emit({
                        eventName: evtTree.SearchBar.FmlmSearch.Click,
                        data: {
                          isTimerVisible: true,
                          resumeSearchData: {
                            from: resumeSearch.origin,
                            fromName: resumeSearch.origin,
                            to: resumeSearch.destination,
                            toName: resumeSearch.destination,
                            passengers: resumeSearch.passengerCounts,
                            outboundDate: dayjs(resumeSearch.departureTime),
                            inboundDate: resumeSearch.arrivalTime ? dayjs(resumeSearch.arrivalTime) : null,
                            fromfmlmSearch: resumeSearch.fromfmlmSearch,
                            tofmlmSearch: resumeSearch.tofmlmSearch,
                            fromGeoPosition: resumeSearch.fromGeoPosition,
                            toGeoPosition: resumeSearch.toGeoPosition
                          }
                        },
                      });
                      return;
                    }

                    showNotification("error", intl.formatMessage({ id: "selectValidOriginAndDestination" }), "", 6);
                    return;
                  }
                  _eventBus.emit({
                    eventName: evtTree.SearchBar.SearchButton.Click,
                    data: {
                      departureTime: resumeSearch.departureTime,
                      arrivalTime: resumeSearch.arrivalTime,
                      origin: resumeSearch?.origin,
                      destination: resumeSearch?.destination,
                      totalPassengerCount: resumeSearch?.totalPassengerCount,
                      passengerCounts: resumeSearch?.passengerCounts,
                      trainType: resumeSearch?.trainType,
                      //isShuttle: isShuttleBooking,
                      resetValue: true,
                      fromShuttle: resumeSearch?.fromShuttle,
                      toShuttle: resumeSearch?.toShuttle,
                      isFromPopUp: false,
                      lastSearchClick: true,
                      intl: intl
                    },
                  });
                  // }
                }}>
                {intl.formatMessage({ id: "resumeSearch" })}
              </Typography.Text>
            </div>
          </div>}

        </div>
      </div>
    </div>
  );
};

export default SearchComponent;
